import {useState, useEffect, useCallback} from 'react'
import {Text, Flex, ModalBody, Button, Tooltip} from '@chakra-ui/react'
import {Icon, QuestionOutlineIcon} from '@chakra-ui/icons'
import {PropTypes} from 'prop-types'
import {useTranslation, Trans} from 'react-i18next'
import {useLocation} from 'react-router'
import useSWR from 'swr'
import useSWRMutation from 'swr/mutation'

import patternBackground from '../../../../../assets/images/pattern_background.svg'
import confetti from '../../../../images/confetti.svg'
import {useRails} from '../../../contexts/rails'
import {toCurrency} from '../../../../utils/formatter'
import {fetcher, defaultHeaders} from '../../../../api/fetcher'
import {camelizeResult} from '../../../../api/serializer'
import {Header} from '../components'
import {cashinStepperKey} from '../../CashinStepper/useStepperData'

const StatisticsBlock = ({title, tooltip, text, fontColor, ...props}) => {
  return (
    <Flex flexDirection="column" {...props}>
      <Flex align="center">
        <Text textTransform="uppercase" color={fontColor} mr={2}>
          {title}
        </Text>
        <Tooltip isDisabled={false} label={tooltip}>
          <Icon as={QuestionOutlineIcon} boxSize="15px" />
        </Tooltip>
      </Flex>
      <Text fontSize="2xl" color={fontColor}>
        {text}
      </Text>
    </Flex>
  )
}

StatisticsBlock.defaultProps = {
  fontColor: 'black',
}

StatisticsBlock.propTypes = {
  title: PropTypes.string.isRequired,
  tooltip: PropTypes.string.isRequired,
  text: PropTypes.oneOfType([PropTypes.string, PropTypes.number]).isRequired,
  fontColor: PropTypes.string,
}

const ConfirmationPageModal = ({onRestart, goToUpdateTemplates}) => {
  const {t} = useTranslation('invitePayers')
  const location = useLocation()
  const {
    organisation: {currentSupplier},
    authenticityToken,
  } = useRails()

  const supplierId = currentSupplier?.id

  const {data: transitionData, isLoading: isLoadingTransition} = useSWR(
    `/api/suppliers/${supplierId}/banking_details_transitions`,
    fetcher,
  )

  const {data: customersData, isLoading: isLoadingCustomers} = useSWR(
    `/api/suppliers/${supplierId}/invite_payers`,
    fetcher,
    {
      use: [camelizeResult],
    },
  )

  const {data: stepperData} = useSWR(cashinStepperKey(supplierId), fetcher)

  const templatesVerified =
    stepperData?.stepper?.invoice_email_template_progress?.status === 'verified'

  const {trigger: completeTransition} = useSWRMutation(
    `/api/suppliers/${supplierId}/banking_details_transitions/complete`,
    async (url) => {
      return fetcher(url, {
        method: 'PUT',
        headers: defaultHeaders({authenticityToken}),
      })
    },
  )

  const [selectedCustomers, setSelectedCustomers] = useState(
    location.state?.selectedCustomers || [],
  )
  const [hasEnriched, setHasEnriched] = useState(false)

  const isCustomerMissingAccountsReceivable = useCallback((customer) => {
    return !customer.accountsReceivable
  }, [])

  const anyCustomerNeedsEnrichment = useCallback(() => {
    return selectedCustomers.some(isCustomerMissingAccountsReceivable)
  }, [selectedCustomers, isCustomerMissingAccountsReceivable])

  const enrichCustomerWithFullData = useCallback(
    (selectedCustomer) => {
      const fullCustomerData = customersData?.customers?.find(
        (c) => c.id === selectedCustomer.id,
      )
      return fullCustomerData || selectedCustomer
    },
    [customersData],
  )

  // Enrich customers once on mount if they're missing accountsReceivable
  // This happens when the modal is launched from the customer page banner,
  // where the customer object doesn't include complete data
  useEffect(() => {
    if (!customersData?.customers || hasEnriched) return

    if (anyCustomerNeedsEnrichment()) {
      const enrichedCustomers = selectedCustomers.map(
        enrichCustomerWithFullData,
      )
      setSelectedCustomers(enrichedCustomers)
    }

    setHasEnriched(true)
  }, [
    customersData,
    selectedCustomers,
    hasEnriched,
    anyCustomerNeedsEnrichment,
    enrichCustomerWithFullData,
  ])

  useEffect(() => {
    completeTransition()
  }, [completeTransition])

  // Show loading while we enrich the customer data
  const isEnriching = !hasEnriched && customersData?.customers

  if (isLoadingTransition || isLoadingCustomers || isEnriching) {
    return (
      <Flex flexDirection="column" px={4}>
        <Header title={t('modal.steps.confirmationPage.title')} />
        <ModalBody py={0}>
          <Flex justify="center" align="center" height="400px">
            <Text>Loading...</Text>
          </Flex>
        </ModalBody>
      </Flex>
    )
  }

  const totalAccountsReceivable = selectedCustomers.reduce(
    (sum, customer) => sum + customer.accountsReceivable.cents,
    0,
  )

  const invitedPercentage =
    transitionData?.data.invited_customers_ids.length > 0
      ? (transitionData.data.invited_customers_ids.length /
          transitionData.data.selectable_customers_ids.length) *
        100
      : 0

  return (
    <Flex flexDirection="column" px={4}>
      <Header title={t('modal.steps.confirmationPage.title')} />
      <ModalBody py={0}>
        <Flex flexDirection="column" gap={5}>
          <Flex
            width="100%"
            height="350px"
            backgroundColor="white"
            justify="space-between"
            px={6}
            py={7}
            gap={28}
          >
            {/* Overall progress section */}
            <Flex flexDirection="column" width="55%">
              <Text
                mb={20}
                fontSize="sm"
                textTransform="uppercase"
                color="neutral.900"
              >
                {t('modal.steps.confirmationPage.overallProgress.title')}
              </Text>
              <Flex justify="space-between">
                <StatisticsBlock
                  title={t(
                    'modal.steps.confirmationPage.totalCustomersInvited.title',
                  )}
                  tooltip={t(
                    'modal.steps.confirmationPage.totalCustomersInvited.tooltip',
                  )}
                  text={t(
                    'modal.steps.confirmationPage.totalCustomersInvited.count',
                    {
                      totalInvitedCount:
                        transitionData?.data.invited_customers_ids.length,
                      totalCustomers:
                        transitionData?.data.selectable_customers_ids.length,
                    },
                  )}
                  fontColor="neutral.900"
                />
                <StatisticsBlock
                  title={t(
                    'modal.steps.confirmationPage.totalReceiveable.title',
                  )}
                  tooltip={t(
                    'modal.steps.confirmationPage.totalReceiveable.tooltip',
                  )}
                  text={toCurrency(
                    transitionData?.data?.total_accounts_receiveable?.cents ||
                      0,
                    '$0,0',
                  )}
                  fontColor="neutral.900"
                />
              </Flex>
              <Flex direction="column" mt={6}>
                <Flex flexDirection="column">
                  <Text fontSize="xs" color="mld.neutral.900" textAlign="right">
                    {t(
                      'modal.steps.confirmationPage.overallProgress.progressLabel',
                      {
                        percentage: Math.round(invitedPercentage),
                      },
                    )}
                  </Text>
                </Flex>
                <Flex alignItems="flex-end">
                  <Flex
                    position="relative"
                    width="100%"
                    height="8px"
                    backgroundColor="mld.neutral.700"
                    overflow="hidden"
                  >
                    <Flex
                      position="absolute"
                      left="0"
                      top="0"
                      width={`${
                        transitionData?.data.selectable_customers_ids.length > 0
                          ? invitedPercentage
                          : 0
                      }%`}
                      height="100%"
                      backgroundColor="mld.neutral.900"
                    />
                  </Flex>
                </Flex>
              </Flex>
              <Text
                mt="auto"
                fontSize="sm"
                color="mld.neutral.800"
                lineHeight="125%"
                fontWeight="medium"
              >
                <Trans
                  t={t}
                  i18nKey="modal.steps.confirmationPage.overallProgress.description"
                  components={{bold: <b />}}
                />
              </Text>
            </Flex>
            {/* Current session section */}
            <Flex
              flexDirection="column"
              backgroundColor="mld.primary.200"
              backgroundImage={confetti}
              backgroundRepeat="no-repeat"
              width="35%"
              p={6}
            >
              <Text textTransform="uppercase">
                {t('modal.steps.confirmationPage.currentSessionTitle')}
              </Text>
              <Flex mt={14} flexDirection="column" gap={5}>
                <StatisticsBlock
                  title={t(
                    'modal.steps.confirmationPage.currentSessionCustomersInvited.title',
                  )}
                  tooltip={t(
                    'modal.steps.confirmationPage.currentSessionCustomersInvited.tooltip',
                  )}
                  text={selectedCustomers.length}
                />
                <StatisticsBlock
                  title={t(
                    'modal.steps.confirmationPage.currentSessionAccountsReceivable.title',
                  )}
                  tooltip={t(
                    'modal.steps.confirmationPage.currentSessionAccountsReceivable.tooltip',
                  )}
                  text={toCurrency(totalAccountsReceivable, '$0,0')}
                />
              </Flex>
            </Flex>
          </Flex>
          {/* Next step block, hidden once the templates are verified */}
          {!templatesVerified && (
            <Flex
              flexDirection="column"
              width="100%"
              height="180px"
              backgroundImage={patternBackground}
              borderRadius="sm"
              p={6}
              gap={6}
            >
              <Text fontSize="sm" textTransform="uppercase">
                {t('modal.steps.confirmationPage.nextStep.title')}
              </Text>
              <Flex justify="space-between">
                <Flex flexDirection="column" width="45%">
                  <Text fontSize="xl">
                    {t('modal.steps.confirmationPage.nextStep.subtitle')}
                  </Text>
                  <Text fontSize="sm">
                    {t('modal.steps.confirmationPage.nextStep.description')}
                  </Text>
                </Flex>
                <Flex alignItems="center" width="50%" gap={5}>
                  <Button
                    className="fs-unmask"
                    variant="primary"
                    onClick={goToUpdateTemplates}
                  >
                    {t(
                      'modal.steps.confirmationPage.nextStep.buttons.updateTemplates',
                    )}
                  </Button>
                  <Button
                    className="fs-unmask"
                    variant="outline"
                    border="1px solid"
                    borderRadius="full"
                    borderColor="primary"
                    textStyle="button"
                    onClick={onRestart}
                  >
                    {t('modal.steps.confirmationPage.nextStep.buttons.restart')}
                  </Button>
                </Flex>
              </Flex>
            </Flex>
          )}
        </Flex>
      </ModalBody>
    </Flex>
  )
}

ConfirmationPageModal.propTypes = {
  onRestart: PropTypes.func.isRequired,
  goToUpdateTemplates: PropTypes.func.isRequired,
}

export default ConfirmationPageModal
